import { useEffect, useRef, useState } from "react";
import { Copy, Download, Send, Share2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HANDLE = process.env.REACT_APP_INSTAGRAM_HANDLE || "@arishacreations";

// Client-side Instagram-square (1080x1080) card generator.
// Composites: product image + gradient scrim + brand mark + product name + price + handle.
export default function IgCard({ product, size = "sm", showPreview = true, caption = "", token = "", canAutoPost = false }) {
  const canvasRef = useRef(null);
  const [note, setNote] = useState("");
  const [posting, setPosting] = useState(false);
  const [ready, setReady] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (!product) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = 1080, H = 1080;
    canvas.width = W; canvas.height = H;
    setReady(false);

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      // background fill
      ctx.fillStyle = "#faf6ef";
      ctx.fillRect(0, 0, W, H);
      // cover the product image into the top ~70% of the card
      const targetH = 760;
      const ratio = img.width / img.height;
      let dw = W, dh = W / ratio;
      if (dh < targetH) { dh = targetH; dw = targetH * ratio; }
      const dx = (W - dw) / 2;
      const dy = 0;
      ctx.drawImage(img, dx, dy, dw, dh);
      // scrim
      const grad = ctx.createLinearGradient(0, targetH - 220, 0, H);
      grad.addColorStop(0, "rgba(28,18,14,0)");
      grad.addColorStop(1, "rgba(28,18,14,0.9)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, targetH - 220, W, H - (targetH - 220));
      // solid ink bottom
      ctx.fillStyle = "#1c120e";
      ctx.fillRect(0, targetH, W, H - targetH);

      // Brand mark (top-left)
      ctx.fillStyle = "rgba(250,246,239,0.95)";
      ctx.textBaseline = "top";
      ctx.font = "600 30px 'Fraunces', Georgia, serif";
      ctx.fillText("ARISHA", 60, 60);
      ctx.font = "500 14px 'DM Sans', system-ui, sans-serif";
      ctx.fillStyle = "#e7ba6b";
      ctx.fillText("C R E A T I O N S", 60, 100);

      // Category eyebrow
      ctx.font = "600 22px 'DM Sans', sans-serif";
      ctx.fillStyle = "#e7ba6b";
      ctx.fillText((product.category || "").toUpperCase(), 60, targetH + 60);

      // Product name (wrap)
      ctx.font = "500 62px 'Fraunces', Georgia, serif";
      ctx.fillStyle = "#fdf3e4";
      const name = product.name || "";
      const maxW = W - 120;
      const words = name.split(" ");
      let line = "", y = targetH + 110;
      for (const w of words) {
        const test = line ? `${line} ${w}` : w;
        if (ctx.measureText(test).width > maxW) {
          ctx.fillText(line, 60, y); y += 70; line = w;
        } else line = test;
      }
      if (line) ctx.fillText(line, 60, y);

      // Price
      ctx.font = "500 48px 'Fraunces', Georgia, serif";
      ctx.fillStyle = "#fdf3e4";
      const priceTxt = `₹${Number(product.price || 0).toLocaleString("en-IN")}`;
      ctx.fillText(priceTxt, 60, H - 130);
      if (product.original_price && product.original_price > product.price) {
        const origTxt = `₹${Number(product.original_price).toLocaleString("en-IN")}`;
        const px = 60 + ctx.measureText(priceTxt).width + 24;
        ctx.font = "400 26px 'DM Sans', sans-serif";
        ctx.fillStyle = "#a89b8f";
        ctx.fillText(origTxt, px, H - 108);
        const w2 = ctx.measureText(origTxt).width;
        ctx.strokeStyle = "#a89b8f"; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(px, H - 92); ctx.lineTo(px + w2, H - 92); ctx.stroke();
      }

      // Handle (bottom-right)
      ctx.font = "500 24px 'DM Sans', sans-serif";
      ctx.fillStyle = "#e7ba6b";
      ctx.textAlign = "right";
      ctx.fillText(HANDLE, W - 60, H - 120);
      ctx.font = "400 18px 'DM Sans', sans-serif";
      ctx.fillStyle = "#c8b39e";
      ctx.fillText("New in the atelier", W - 60, H - 84);
      ctx.textAlign = "left";

      setPreviewUrl(canvas.toDataURL("image/png"));
      setReady(true);
    };
    img.onerror = () => setReady(false);
    img.src = product.image;
  }, [product]);

  const download = () => {
    if (!ready) return;
    const url = canvasRef.current.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `arisha-${(product.id || "card").replace(/[^a-z0-9]/gi, "")}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const copyCaption = async () => {
    if (!caption) return;
    try { await navigator.clipboard.writeText(caption); setNote("Caption copied, paste it in Instagram."); } catch { setNote("Couldn't copy. Select the caption text and copy it."); }
  };

  const autoPost = async () => {
    if (!ready || posting) return;
    setPosting(true); setNote("Posting to Instagram…");
    try {
      const blob = await new Promise(res => canvasRef.current.toBlob(res, "image/png"));
      const form = new FormData();
      form.append("file", blob, `arisha-${product.id}.png`);
      form.append("caption", caption);
      form.append("base_url", window.location.origin);
      form.append("product_id", product.id);
      await axios.post(`${API}/admin/instagram/publish`, form, { headers: { "X-Admin-Token": token } });
      setNote("Posted to Instagram.");
    } catch (err) { setNote(err?.response?.data?.detail || "Posting failed. Try again."); } finally { setPosting(false); }
  };

  const share = async () => {
    if (!ready) return;
    await copyCaption();
    try {
      const blob = await new Promise(res => canvasRef.current.toBlob(res, "image/png"));
      if (navigator.canShare && blob) {
        const file = new File([blob], `arisha-${product.id}.png`, { type: "image/png" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: product.name, text: caption || `${product.name} · ₹${product.price}` });
          return;
        }
      }
      download();
    } catch (err) { download(); }
  };

  return (
    <div className={`ig-card ig-card-${size}`} data-testid={`ig-card-${product?.id}`}>
      <canvas ref={canvasRef} style={{ display: "none" }} />
      {showPreview && previewUrl && <img src={previewUrl} alt="Instagram card preview" className="ig-preview" />}
      <div className="ig-actions">
        <button type="button" onClick={download} disabled={!ready} data-ig-download data-testid={`ig-download-${product?.id}`}>
          <Download size={13} /> Download PNG
        </button>
        <button type="button" onClick={share} disabled={!ready} data-testid={`ig-share-${product?.id}`}>
          <Share2 size={13} /> Share
        </button>
        {caption && <button type="button" onClick={copyCaption} data-testid={`ig-copy-caption-${product?.id}`}><Copy size={13} /> Caption</button>}
      </div>
      {canAutoPost && <button type="button" className="ig-autopost" onClick={autoPost} disabled={!ready || posting} data-testid={`ig-autopost-${product?.id}`}><Send size={13} /> {posting ? "Posting…" : "Post now"}</button>}
      {caption && <textarea className="ig-caption" readOnly rows={5} value={caption} data-testid={`ig-caption-${product?.id}`} />}
      {note && <small className="ig-note" data-testid={`ig-note-${product?.id}`}>{note}</small>}
    </div>
  );
}
